/* ==================================================================
   CROKETTE-LS — behaviour
   Vanilla, dependency-free, and safe to run more than once (the theme
   editor re-renders sections on every setting change, so each
   initialiser marks its root with a data flag and bails if it's
   already wired up).

   Contains:
     1. gallery      — snap-scroll track, thumbs, arrows, progress, counter
     2. variant picker — price + hidden variant id + URL, sold-out states
     3. accordions   — animated height, one-at-a-time optional
     4. reveal       — IntersectionObserver fade-up for editorial blocks
     5. recommended  — tabs + snap carousel with progress + counter
   ================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- small helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function once(el, flag) {
    if (el.dataset[flag] === 'on') return false;
    el.dataset[flag] = 'on';
    return true;
  }

  /* =================================================================
     1. GALLERY
     ================================================================= */
  function initGallery(root) {
    if (!once(root, 'cklsGallery')) return;

    var track = $('[data-ckls-track]', root);
    if (!track) return;
    var slides = $$('.ckls-gallery__slide', track);
    var thumbs = $$('[data-ckls-thumb]', root);
    var prev = $('[data-ckls-prev]', root);
    var next = $('[data-ckls-next]', root);
    var fill = $('[data-ckls-fill]', root);
    var count = $('[data-ckls-count]', root);
    var total = slides.length;
    if (!total) return;

    var index = 0;

    function render() {
      if (fill) {
        fill.style.width = (100 / total) + '%';
        fill.style.transform = 'translateX(' + (index * 100) + '%)';
      }
      if (count) count.textContent = (index + 1) + '/' + total;
      thumbs.forEach(function (t, i) { t.setAttribute('aria-current', i === index ? 'true' : 'false'); });
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === total - 1;
    }

    function goTo(i, smooth) {
      index = Math.max(0, Math.min(total - 1, i));
      track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: (smooth && !reduceMotion) ? 'smooth' : 'auto' });
      render();
    }

    // keep the index in sync when the user swipes / scrolls the track
    var ticking;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var mid = track.scrollLeft + track.clientWidth / 2;
        var nearest = 0;
        var best = Infinity;
        slides.forEach(function (s, i) {
          var c = s.offsetLeft - track.offsetLeft + s.offsetWidth / 2;
          var d = Math.abs(c - mid);
          if (d < best) { best = d; nearest = i; }
        });
        if (nearest !== index) { index = nearest; render(); }
      });
    }, { passive: true });

    if (prev) prev.addEventListener('click', function () { goTo(index - 1, true); });
    if (next) next.addEventListener('click', function () { goTo(index + 1, true); });
    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { goTo(i, true); });
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1, true); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1, true); }
    });

    // jump the gallery to the image for a variant when one is picked
    root.addEventListener('ckls:variant', function (e) {
      var mediaId = e.detail && e.detail.mediaId;
      if (!mediaId) return;
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].dataset.mediaId === String(mediaId)) { goTo(i, true); return; }
      }
    });

    render();
  }

  /* =================================================================
     2. VARIANT PICKER — button chips, one row per product option

     Replaces the old <select>. Each option renders as a radiogroup of
     chips; the selected combination is resolved against the variant
     table written into the page as JSON. Combinations that do not
     exist, or that are sold out, are marked so the customer can see
     what is gone without having to click it.
     ================================================================= */
  function initPicker(root) {
    if (!once(root, 'cklsPicker')) return;

    var payload = $('[data-ckls-variant-data]', root);
    if (!payload) return;

    var data;
    try { data = JSON.parse(payload.textContent); } catch (err) { return; }
    var variants = (data && data.variants) || [];
    if (!variants.length) return;

    var section = root.closest('[data-ckls-section]') || document;
    var priceEl = $('[data-ckls-price]', section);
    var compareEl = $('[data-ckls-compare]', section);
    var hidden = $('[data-ckls-variant-id]', section);
    var actions = $('[data-ckls-add]', section);
    var button = actions ? actions.querySelector('button[name="add"]') : null;
    var gallery = $('[data-ckls-gallery]', section);
    var stockEl = $('[data-ckls-stock]', root);

    var addText = (actions && actions.dataset.cklsAddText) || 'Add to bag';
    var soldOutText = (actions && actions.dataset.cklsSoldOutText) || 'Sold out';
    var chooseText = (actions && actions.dataset.cklsChooseText) || 'Choose a size';

    var lowStockAt = parseInt(root.dataset.lowStockThreshold, 10);
    if (!isFinite(lowStockAt)) lowStockAt = 0;
    var lowStockText = root.dataset.lowStockText || '';

    var groups = $$('[data-ckls-opt]', root);
    var optionCount = data.optionCount || groups.length;

    // current selection, one entry per option position
    var chosen = [];
    groups.forEach(function (group, i) {
      var checked = group.querySelector('[data-ckls-chip][aria-checked="true"]');
      chosen[i] = checked ? checked.dataset.value : null;
    });

    /* ---- variant lookup ---- */
    function findVariant(values) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var j = 0; j < values.length; j++) {
          if (values[j] === null || String(v.options[j]) !== String(values[j])) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    // does any variant exist for this option value, given the other picks?
    function stateFor(groupIndex, value) {
      var exists = false;
      var available = false;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (String(v.options[groupIndex]) !== String(value)) continue;
        var compatible = true;
        for (var j = 0; j < optionCount; j++) {
          if (j === groupIndex) continue;
          if (chosen[j] !== null && chosen[j] !== undefined && String(v.options[j]) !== String(chosen[j])) {
            compatible = false;
            break;
          }
        }
        if (!compatible) continue;
        exists = true;
        if (v.available) { available = true; break; }
      }
      return { exists: exists, available: available };
    }

    function currentVariant() {
      if (data.singleDefault) return variants[0];
      for (var i = 0; i < optionCount; i++) {
        if (chosen[i] === null || chosen[i] === undefined) return null;
      }
      return findVariant(chosen);
    }

    /* ---- the theme's add-to-cart snippet wraps its label in a span ---- */
    function setLabel(text) {
      if (!button) return;
      var label = button.querySelector('.add-to-cart-text');
      if (label) { label.textContent = text; return; }
      // the label is a bare text node beside the icon / checkmark-burst spans —
      // rewrite that node only, never the spans
      var node = null;
      for (var i = 0; i < button.childNodes.length; i++) {
        var child = button.childNodes[i];
        if (child.nodeType === 3 && child.textContent.trim()) { node = child; break; }
      }
      if (node) {
        node.textContent = text;
      } else {
        button.insertBefore(document.createTextNode(text), button.querySelector('.add-to-cart__added'));
      }
    }

    function renderStock(v) {
      if (!stockEl) return;
      if (!v || !v.available || !lowStockAt || !lowStockText) { stockEl.hidden = true; return; }
      // a variant with inventory not tracked reports null — never warn on those
      if (!v.inventory_managed || v.inventory === null || v.inventory === undefined) { stockEl.hidden = true; return; }
      if (v.inventory > lowStockAt || v.inventory <= 0) { stockEl.hidden = true; return; }
      stockEl.textContent = lowStockText.replace('[count]', v.inventory);
      stockEl.hidden = false;
    }

    function renderChips() {
      groups.forEach(function (group, gi) {
        var chosenLabel = $('[data-ckls-opt-chosen]', group);
        if (chosenLabel) chosenLabel.textContent = chosen[gi] || '';

        $$('[data-ckls-chip]', group).forEach(function (chip) {
          var state = stateFor(gi, chip.dataset.value);
          var isOn = chosen[gi] !== null && chip.dataset.value === chosen[gi];

          chip.setAttribute('aria-checked', isOn ? 'true' : 'false');
          chip.tabIndex = isOn ? 0 : -1;
          chip.classList.toggle('is-selected', isOn);
          chip.classList.toggle('is-unavailable', !state.available);
          chip.classList.toggle('is-nonexistent', !state.exists);
          // sold-out sizes stay clickable and announce themselves rather
          // than disappearing — customers look for their size either way
          chip.setAttribute('aria-disabled', state.available ? 'false' : 'true');
          var slash = chip.querySelector('.ckls-chip__slash');
          if (slash) slash.hidden = state.available;
        });

        // keep one chip in the tab order even before anything is picked
        var focusable = group.querySelector('[data-ckls-chip][tabindex="0"]');
        if (!focusable) {
          var first = group.querySelector('[data-ckls-chip]');
          if (first) first.tabIndex = 0;
        }
      });
    }

    function apply(pushUrl) {
      renderChips();
      var v = currentVariant();

      if (!v) {
        if (hidden) hidden.value = '';
        if (button) button.disabled = true;
        setLabel(chooseText);
        renderStock(null);
        return;
      }

      if (hidden) hidden.value = v.id;
      if (priceEl) priceEl.innerHTML = v.price;
      if (compareEl) {
        compareEl.innerHTML = v.compare_at || '';
        compareEl.hidden = !v.compare_at;
      }
      if (button) button.disabled = !v.available;
      setLabel(v.available ? addText : soldOutText);
      renderStock(v);

      if (gallery && v.media_id) {
        gallery.dispatchEvent(new CustomEvent('ckls:variant', { detail: { mediaId: v.media_id } }));
      }
      // tell the rest of the page (delivery estimate, pickup card, …)
      section.dispatchEvent(new CustomEvent('ckls:variant:change', { detail: { variant: v }, bubbles: true }));

      if (pushUrl && v.url && window.history && window.history.replaceState) {
        window.history.replaceState({}, '', v.url);
      }
    }

    /* ---- interaction ---- */
    groups.forEach(function (group, gi) {
      var chips = $$('[data-ckls-chip]', group);

      chips.forEach(function (chip, ci) {
        chip.addEventListener('click', function () {
          if (chip.classList.contains('is-nonexistent')) {
            // the combination does not exist at all — reset the other
            // options rather than leaving the customer in a dead end
            for (var j = 0; j < optionCount; j++) { if (j !== gi) chosen[j] = null; }
          }
          chosen[gi] = chip.dataset.value;
          apply(true);
          chip.focus();
        });

        // arrow keys move within the group, like a real radiogroup
        chip.addEventListener('keydown', function (e) {
          var dir = 0;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') dir = 1;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') dir = -1;
          if (!dir) return;
          e.preventDefault();
          var nextIndex = (ci + dir + chips.length) % chips.length;
          chips[nextIndex].click();
        });
      });
    });

    // a product with a single default variant has no chips to pick
    if (data.singleDefault) { apply(false); return; }

    apply(false);
  }

  /* =================================================================
     3. ACCORDIONS
     ================================================================= */
  function initAccordions(root) {
    if (!once(root, 'cklsAcc')) return;

    var single = root.dataset.cklsSingle === 'true';
    var items = $$('[data-ckls-acc]', root);

    items.forEach(function (item) {
      var head = $('[data-ckls-acc-head]', item);
      var panel = $('[data-ckls-acc-panel]', item);
      if (!head || !panel) return;

      function open() {
        head.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        panel.style.height = reduceMotion ? 'auto' : panel.scrollHeight + 'px';
        if (!reduceMotion) {
          panel.addEventListener('transitionend', function handler(e) {
            if (e.propertyName !== 'height') return;
            panel.style.height = 'auto';
            panel.removeEventListener('transitionend', handler);
          });
        }
      }
      function close() {
        head.setAttribute('aria-expanded', 'false');
        if (reduceMotion) { panel.style.height = '0px'; panel.hidden = true; return; }
        panel.style.height = panel.scrollHeight + 'px';
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () { panel.style.height = '0px'; });
        });
        panel.addEventListener('transitionend', function handler(e) {
          if (e.propertyName !== 'height') return;
          panel.hidden = true;
          panel.removeEventListener('transitionend', handler);
        });
      }

      head.addEventListener('click', function () {
        var isOpen = head.getAttribute('aria-expanded') === 'true';
        if (single && !isOpen) {
          items.forEach(function (other) {
            if (other === item) return;
            var h = $('[data-ckls-acc-head]', other);
            if (h && h.getAttribute('aria-expanded') === 'true') h.click();
          });
        }
        isOpen ? close() : open();
      });

      // start closed unless the merchant set it open by default
      if (head.getAttribute('aria-expanded') === 'true') {
        panel.hidden = false;
        panel.style.height = 'auto';
      } else {
        panel.hidden = true;
        panel.style.height = '0px';
      }
    });
  }

  /* =================================================================
     4. SCROLL REVEAL
     ================================================================= */
  var observer = null;
  function initReveal(root) {
    if (!once(root, 'cklsReveal')) return;
    var targets = $$('.ckls-reveal', root);
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (t) { t.classList.add('is-in'); });
      return;
    }
    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });
    }
    targets.forEach(function (t) { observer.observe(t); });
  }

  /* =================================================================
     5. RECOMMENDED — tabs + carousel
     ================================================================= */
  function wireCarousel(panel, shared) {
    var track = $('[data-ckls-rec-track]', panel);
    if (!track) return null;
    var cards = $$('.ckls-card', track);
    if (!cards.length) return null;

    // a hidden panel measures 0, which would poison every ratio below,
    // so every measurement falls back to a sane number
    function perView() {
      var cardW = cards[0].offsetWidth;
      var trackW = track.clientWidth;
      if (!cardW || !trackW) return 1;
      return Math.max(1, Math.min(cards.length, Math.round(trackW / cardW)));
    }
    function pages() { return Math.max(1, cards.length - perView() + 1); }
    function page() {
      if (cards.length < 2) return 0;
      var step = cards[1].offsetLeft - cards[0].offsetLeft;
      if (!step) return 0;
      var p = Math.round(track.scrollLeft / step);
      return isFinite(p) ? Math.max(0, p) : 0;
    }

    function render() {
      var total = pages();
      var i = Math.min(page(), total - 1);
      if (!isFinite(i) || i < 0) i = 0;
      if (shared.fill) {
        shared.fill.style.width = (100 / total) + '%';
        shared.fill.style.transform = 'translateX(' + (i * 100) + '%)';
      }
      if (shared.count) shared.count.textContent = (i + 1) + '/' + cards.length;
      var atStart = track.scrollLeft <= 2;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      if (shared.prev) shared.prev.disabled = atStart;
      if (shared.next) shared.next.disabled = atEnd;
    }

    function nudge(dir) {
      var step = cards.length > 1 ? (cards[1].offsetLeft - cards[0].offsetLeft) : track.clientWidth;
      track.scrollBy({ left: dir * step * Math.max(1, perView() - 1), behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    var ticking;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { ticking = false; render(); });
    }, { passive: true });
    window.addEventListener('resize', render);

    return { render: render, nudge: nudge };
  }

  function initRecommended(root) {
    if (!once(root, 'cklsRec')) return;

    var tabs = $$('[data-ckls-rec-tab]', root);
    var panels = $$('[data-ckls-rec-panel]', root);
    var shared = {
      fill: $('[data-ckls-rec-fill]', root),
      count: $('[data-ckls-rec-count]', root),
      prev: $('[data-ckls-rec-prev]', root),
      next: $('[data-ckls-rec-next]', root)
    };

    var active = null;
    var carousels = panels.map(function (p) { return wireCarousel(p, shared); });

    function show(i) {
      tabs.forEach(function (t, j) { t.setAttribute('aria-selected', j === i ? 'true' : 'false'); });
      panels.forEach(function (p, j) { p.hidden = j !== i; });
      active = carousels[i];
      if (active) active.render();
    }

    tabs.forEach(function (t, i) { t.addEventListener('click', function () { show(i); }); });
    if (shared.prev) shared.prev.addEventListener('click', function () { if (active) active.nudge(-1); });
    if (shared.next) shared.next.addEventListener('click', function () { if (active) active.nudge(1); });

    show(0);
  }

  /* =================================================================
     BOOT
     ================================================================= */
  function boot(scope) {
    var ctx = scope || document;
    $$('[data-ckls-gallery]', ctx).forEach(initGallery);
    $$('[data-ckls-picker]', ctx).forEach(initPicker);
    $$('[data-ckls-accordions]', ctx).forEach(initAccordions);
    $$('[data-ckls-reveal-root]', ctx).forEach(initReveal);
    $$('[data-ckls-rec]', ctx).forEach(initRecommended);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // theme editor: re-run after Shopify swaps a section's markup
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) { boot(e.target); });
})();
